//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Avatar, makeStyles, DialogTitle, DialogContent, DialogActions, Dialog, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AttachmentIcon from '@material-ui/icons/Attachment';
import SendIcon from '@material-ui/icons/Send';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logEvent, unsubscribeRealtimeUpdates } from '../../system/firebase';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { LayoutContext, AppContext } from '../../common/contexts';
import { getNameByUser } from '../../common/textHelper';
import { PLACEHOLDER_FIELD_TO, PLACEHOLDER_FIELD_JOB_TITLE } from '../../common/placeholderHelper';
import useRequiredFields from '../../common/hooks/useRequiredFields';
import { sendNewExternalThreadMessage, uploadThreadAttachment } from '../../common/services/empDashboardServices';
import { getEmployerActiveJobsSuggestions } from '../../common/services/talentCenterServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

import { ANALYTICS_EVENT_EXTERNAL_MESSAGE_THREAD_CREATED, BASE_URL, DASHBOARD_ID_MY_SETTINGS, DASHBOARD_ID_MY_SETTINGS_SUBSCRIPTION } from '../../common/constants';
import { PATH_DASHBOARD } from '../../system/constants';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdAutocomplete from '../../common/elemental/AfitdAutocomplete';
import AfitdFileTypeIcon from '../../common/elemental/AfitdFileTypeIcon';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _find from 'lodash/find';
import _isNil from 'lodash/isNil';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    fieldContainerGroup: {
        display: 'flex',
        margin: '10px 0',
        alignItems: 'center',
    },
    fieldContainer: { flex: 1, padding: '0 5px' },
    // fieldLabel: { fontWeight: 'bold', margin: '10px 0' },
    fieldLabel: { padding: '5px 0', marginTop: 20, fontWeight: 'bold' },
    messageField: { [theme.breakpoints.up('md')]: { minWidth: 600 } },
    buttonsContainer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', margin: 16 },
    titleContainer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 'bold' },
    attachmentContainer: { display: 'flex', alignItems: 'center', marginRight: 5 },
    closeIcon: { cursor: 'pointer' },
    cancelButton: { marginRight: 16 },
    attachmentIcon: { cursor: 'pointer', border: '2px solid #F0F2F6', padding: 5, borderRadius: 50, color: 'rgba(17, 30, 20, 0.4)', fontSize: 35 },
    attachmentIconContainer: { padding: 0, marginRight: 16 },

    innerContainer: { padding: 20, border: '1px solid #D5D7DD', borderRadius: 4, marginTop: 10, background: 'rgba(255,255,255,0.9)' },
    fileTitle: { fontWeight: 'bold' },
    fileSize: { color: 'rgba(17, 30, 20, 0.4)' },
    inline: { display: 'flex', alignItems: 'center' },
    fileTextContainer: { marginLeft: 20 },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        props,
        classes,
        employerId,
        esToken,
        setEsToken,
        isDirty,
        jobSeekerId,
        jobSeekerName,
        jobSeekerProfilePicUrl,
        handleChangeSendTo,
        handleChangeJobTitle,
        handleChangeMessage,
        handleCloseDialog,
        handleClickSendButton,
        cleanUpAndClose,
        // message,
        // setMessage,
        handleChangeAttachment,
        // jobSeekerNames,
        // setJobSeekerNames,
        jobSeekers,
        subject,
        // setSubject,
        activeJobOptions,
        setActiveJobOptions,
        // file,
        cancelModal,
        setCancelModal,
        limitModalOpen,
        setLimitModalOpen,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,
        getFieldError,
        onConfirmJsEnagageLimitDialog,
    } = params;

    const { open, jobSeekerId: jobSeekerIdProp, jobId: jobIdProp } = props;

    const recipient = { uid: jobSeekerId, jobSeekerName, profilePicUrl: jobSeekerProfilePicUrl }; //jobSeeker?.data;
    const recipientName = jobSeekerName;
    const recipientProfilePicUrl = jobSeekerProfilePicUrl; //recipient?.profilePicUrl;
    const recipientDisabled = jobSeekerIdProp !== undefined;

    const subjectDisabled = jobIdProp !== undefined;

    const sendButtonDisabled = !isDirty && (jobSeekerIdProp === undefined || jobIdProp === undefined);

    const defns = {
        root: { open, maxWidth: 'md', disableBackdropClick: true, disableEscapeKeyDown: true },
        sendButton: { onClick: handleClickSendButton, label: 'Send', startIcon: <SendIcon />, color: 'secondary', disabled: sendButtonDisabled },
        cancelButton: { onClick: handleCloseDialog, label: 'Cancel' },
        fieldLabel: { variant: 'body1' },
        attachmentText: { variant: 'body1' },
        messageField: { placeholder: 'Type your message...', multiline: true, rows: 3, rowsMax: 5, onChange: handleChangeMessage, error: getFieldError('message') },
        closeIcon: { onClick: handleCloseDialog },
        attachmentInput: { type: 'file', hidden: true, onChange: handleChangeAttachment, accept: '.jpg, .png' },
        attachmentIconContainer: { component: 'label' },
        sendTo: {
            disabled: recipientDisabled,
            closeIcon: false,
            ChipProps: {
                onDelete: null, //() => setData(prev => ({ ...prev, jobSeekerNames: [] })),
                deleteIcon: <CloseIcon />,
                label: recipientName,
                avatar: <Avatar src={recipientProfilePicUrl} />,
            },
            multiple: true, // set to multiple so the Chip is displayed but it is actually a single-select
            options: recipientDisabled ? [] : jobSeekers,
            value: [recipient],
            getOptionLabel: option => getNameByUser(option),
            renderInput: params => <AfitdTextField {...params} placeholder={recipient ? null : PLACEHOLDER_FIELD_TO} color="secondary" />,
            onChange: handleChangeSendTo,
            error: getFieldError('sendTo'),
        },
        subject: {
            disabled: subjectDisabled,
            options: activeJobOptions,
            value: subject,
            getOptionLabel: option => option?.jobTitle || option,
            getOptionSelected: (option, value) => option?.jobTitle === value,
            placeholder: PLACEHOLDER_FIELD_JOB_TITLE,
            onChange: handleChangeJobTitle,
            error: getFieldError('subject'),
            onSuggestionsFetchRequested: async value => {
                const keywords = value ? value : undefined;
                const options = await getEmployerActiveJobsSuggestions({ keywords, employerId, esToken, setEsToken });
                setActiveJobOptions(options);
            },
        },
        confirmCancelDialog: {
            title: 'Cancel',
            open: cancelModal,
            onConfirm: () => {
                setCancelModal(false);
                cleanUpAndClose();
            },
            onClose: () => setCancelModal(false),
        },
        limitDialog: {
            open: limitModalOpen,
            onClose: () => setLimitModalOpen(false),
            onConfirm: onConfirmJsEnagageLimitDialog,
            yesButtonLabel: 'Upgrade',
            yesButtonColor: 'secondary',
            message: 'You have reached your limit for candidate engagement for this Tier.  Please click below to Upgrade your subscription',
            title: 'Interaction Limit Exceeded',
        },
        fileTitle: { variant: 'body2' },
        fileSize: { variant: 'caption' },
        pageErrorDialog: {
            message: 'You must enter all required fields.',
            open: pageErrorDialogOpen,
            onClose: () => setPageErrorDialogOpen(false),
        },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SendMessageDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const history = useHistory();
    const classes = useStyles();
    const { setSnackbarErrorMessage, setSnackbarMessage } = useContext(LayoutContext);
    const { userId, employerId, currentEmployer, sys, esToken, setEsToken } = useContext(AppContext);
    const unsubscribeOnSnapshotListRef = useRef([]);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const {
        // open,
        onClose,
        conversations = [],
        jobSeekerId: jobSeekerIdProp,
        jobSeekerName: jobSeekerNameProp,
        jobSeekerProfilePicUrl: jobSeekerProfilePicUrlProp,
        jobId: jobIdProp,
        jobTitle: jobTitleProp = null, // set default so it won't pass undefined to Autocomplete causing component to be uncontrolled
    } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [jobSeekers, setJobSeekers] = useState([]);
    const [activeJobOptions, setActiveJobOptions] = useState();
    const [message, setMessage] = useState();
    const [jobSeekerNames, setJobSeekerNames] = useState([]);
    const [subject, setSubject] = useState(jobTitleProp);
    const [jobId, setJobId] = useState(jobIdProp);
    const [cancelModal, setCancelModal] = useState(false);
    const [limitModalOpen, setLimitModalOpen] = useState(false);
    const [pageErrorDialogOpen, setPageErrorDialogOpen] = useState(false);
    const { /*invalidRequiredFields, setInvalidRequiredFields,*/ getFieldError, clearInvalidRequiredField, validateRequiredFields } = useRequiredFields();
    const [isOkToSave, setIsOkToSave] = useState(false);
    const [file, setFile] = useState(null);

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------
    const jobSeekerId = jobSeekerIdProp;
    const jobSeekerName = jobSeekerNameProp; //getNameByUser(_get(jobSeeker, 'data'));
    const jobSeekerProfilePicUrl = jobSeekerProfilePicUrlProp;

    //---------------------------------------------------------------------
    // Engage check
    //---------------------------------------------------------------------
    const tiers = _get(sys, 'tiers', []);
    const subscribedTier = _get(currentEmployer, 'tier', 0);
    const companyName = _get(currentEmployer, 'companyName');
    const companyProfilePicUrl = _get(currentEmployer, 'profilePicUrl');
    // const tierNew = _get(currentEmployer, 'tierNew', 0);
    // const subscribedTierDetails = tiers.find(el => el.tier === subscribedTier);
    // const billCycleEndDt = currentEmployer?.billCycleEndDt.toDate ? currentEmployer?.billCycleEndDt.toDate() : new Date();
    const subscribedTierDetails = _find(tiers, { tier: subscribedTier });
    const activeJobMax = _get(subscribedTierDetails, 'activeJobMax');
    const jsEngageMax = _get(subscribedTierDetails, 'jsEngageMax');

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const getPageIsValid = useCallback(() => {
        const requiredFields = { jobSeekerName, subject, message };

        return validateRequiredFields(requiredFields);
    }, [jobSeekerName, subject, message, validateRequiredFields]);

    const cleanUpAndClose = useCallback(() => {
        if (unsubscribeOnSnapshotListRef.current) {
            unsubscribeRealtimeUpdates(unsubscribeOnSnapshotListRef.current);
            unsubscribeOnSnapshotListRef.current = [];
        }
        if (onClose) onClose();
    }, [onClose]);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // load default job title suggestions if it has not been done so
    useEffect(() => {
        const loadInitialJobTitlesSuggestions = async () => {
            const options = await getEmployerActiveJobsSuggestions({ employerId, esToken, setEsToken });
            setActiveJobOptions(options);
        };
        if (_isNil(activeJobOptions)) {
            loadInitialJobTitlesSuggestions();
        }
    }, [employerId, activeJobOptions, setActiveJobOptions, esToken, setEsToken]);

    // useEffect(() => {
    //     if (open) {
    //         unsubscribeOnSnapshotListRef.current.push(getJobSeekers(setJobSeekers));
    //         unsubscribeOnSnapshotListRef.current.push(fetchEmployerActiveJobs(setActiveJobs, employerId));
    //     }
    //     //eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [open]);

    useEffect(() => {
        //
        //TODO Update and fix blockedDt logic
        let blockedJobSeekers = conversations.filter(el => el?.blocked?.jobSeeker === true).map(el => el.members[el.jobSeekerId]?.uid);
        let filteredJobSeekers = jobSeekers.filter(el => !blockedJobSeekers.includes(el.uid));
        if (filteredJobSeekers.length !== jobSeekers.length) {
            setJobSeekers(filteredJobSeekers);
        }
    }, [jobSeekers, conversations]);

    // useEffect(() => {
    //     if (jobSeekerNames.length) {
    //         const [recipient] = jobSeekerNames;
    //         let messagedJobs = conversations.filter(el => el.jobSeekerId === recipient.uid).map(el => el.jobId);
    //         let filteredJobs = activeJobs.filter(el => !messagedJobs.includes(el.uid));

    //         if (filteredJobs.length !== activeJobs.length) {
    //             // setActiveJobs(filteredJobs);
    //         }
    //     }
    // }, [jobSeekerNames, conversations, activeJobs]);

    useEffect(() => {
        let payload = {
            messageDto: {
                jobSeekerId,
                content: message,
                createDt: new Date(),
                senderId: userId,
                first: true,
            },
            activeJobMax,
            jsEngageMax,
            employerId,
            tier: subscribedTier,
            companyName: companyName,
            companyProfilePicUrl,
            jobSeekerId,
            jobSeekerName,
            jobSeekerProfilePicUrl,
            jobId,
            jobTitle: subject,
            setSnackbarMessage,
            setSnackbarErrorMessage,
        };

        const sendNewMessage = async () => {
            const resp = await sendNewExternalThreadMessage(payload);

            if (resp?.success) {
                // clean up and close
                cleanUpAndClose();
                resp?.isNewThread && logEvent(ANALYTICS_EVENT_EXTERNAL_MESSAGE_THREAD_CREATED);
            } else {
                // if failed with error, handle it.
                const errorCode = resp?.errorCode;
                if (errorCode) {
                    if (errorCode === 'engagement-limit-exceeded') {
                        setLimitModalOpen(true);
                    }
                }
            }
        };

        const sendNewMessageWithFile = async () => {
            uploadThreadAttachment({
                file,
                senderId: employerId,
                onAttachmentUploadComplete: async (file, url) => {
                    const { size, type, name } = file;
                    payload.messageDto.attachment = {
                        url,
                        title: name,
                        type,
                        size,
                    };
                    sendNewMessage();
                },
            });
        };

        if (isOkToSave) {
            if (file) sendNewMessageWithFile();
            else sendNewMessage();
        }

        setIsOkToSave(false);
    }, [
        activeJobMax,
        jsEngageMax,
        employerId,
        subscribedTier,
        companyName,
        companyProfilePicUrl,
        jobId,
        subject,
        jobSeekerId,
        jobSeekerName,
        jobSeekerProfilePicUrl,
        userId,
        message,
        file,
        isOkToSave,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        cleanUpAndClose,
    ]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleChangeSendTo = (evt, arr) => {
        let options = [arr[arr.length - 1]].filter(el => el);
        setJobSeekerNames(options.length > 0 ? options : []);
        setIsDirty(true);
        // clear error highlight
        clearInvalidRequiredField('sendTo');
    };

    const handleChangeJobTitle = (evt, job) => {
        const jobId = _get(job, 'uid', null);
        const jobTitle = _get(job, 'jobTitle', null);
        setJobId(jobId);
        setSubject(jobTitle);
        setIsDirty(true);
        // clear error highlight
        clearInvalidRequiredField('subject');
    };

    const handleChangeMessage = evt => {
        setMessage(evt.target.value);
        setIsDirty(true);
        // clear error highlight
        clearInvalidRequiredField('message');
    };

    const handleCloseDialog = () => {
        if (isDirty) {
            setCancelModal(true);
        } else {
            cleanUpAndClose();
        }
    };

    const handleClickSendButton = () => {
        if (getPageIsValid()) {
            setIsOkToSave(true);
        } else {
            setPageErrorDialogOpen(true);
        }
    };

    const handleChangeAttachment = evt => {
        setFile(evt.target.files[0]);
    };

    const onConfirmJsEnagageLimitDialog = () => {
        setLimitModalOpen(false);
        history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}/${DASHBOARD_ID_MY_SETTINGS_SUBSCRIPTION}`);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        props,
        classes,
        employerId,
        esToken,
        setEsToken,
        isDirty,
        jobSeekerId,
        jobSeekerName,
        jobSeekerProfilePicUrl,
        handleChangeSendTo,
        handleChangeJobTitle,
        handleChangeMessage,
        handleCloseDialog,
        handleClickSendButton,
        cleanUpAndClose,
        message,
        setMessage,
        handleChangeAttachment,
        file,
        jobSeekerNames,
        jobSeekers,
        setJobSeekerNames,
        subject,
        setSubject,
        activeJobOptions,
        setActiveJobOptions,
        cancelModal,
        setCancelModal,
        limitModalOpen,
        setLimitModalOpen,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,
        getFieldError,
        onConfirmJsEnagageLimitDialog,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <>
            <Dialog {...defns.root}>
                <DialogTitle>
                    <div {...defns.titleContainer}>
                        New Message
                        <CloseIcon {...defns.closeIcon} />
                    </div>
                </DialogTitle>
                <DialogContent>
                    <div {...defns.fieldContainer}>
                        <AfitdTypography {...defns.fieldLabel}>To: *</AfitdTypography>
                        <AfitdAutocomplete {...defns.sendTo} />
                    </div>
                    <div {...defns.fieldContainer}>
                        <AfitdTypography {...defns.fieldLabel}>Subject: *</AfitdTypography>
                        <AfitdAutocomplete {...defns.subject} />
                    </div>
                    <div {...defns.fieldContainer}>
                        <AfitdTypography {...defns.fieldLabel}>Message: *</AfitdTypography>
                        <AfitdTextField {...defns.messageField} />
                    </div>
                    <div {...defns.fieldContainer}>
                        {file?.name && (
                            <div {...defns.innerContainer}>
                                <div {...defns.inline}>
                                    <a href="https://www.google.com/" target="_blank" rel="noopener noreferrer">
                                        <AfitdFileTypeIcon label={file?.type.split('/')[1].toUpperCase()} />
                                    </a>
                                    <div {...defns.fileTextContainer}>
                                        <AfitdTypography {...defns.fileTitle}>{file?.name}</AfitdTypography>
                                        <AfitdTypography {...defns.fileSize}>
                                            {file?.type.split('/')[1].toUpperCase()} {file?.type.split('/')[0]} {(file?.size / 1000).toFixed(2)} kB
                                        </AfitdTypography>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <div {...defns.buttonsContainer}>
                        <div {...defns.attachmentContainer}>
                            <IconButton {...defns.attachmentIconContainer}>
                                <AttachmentIcon {...defns.attachmentIcon} />
                                <input {...defns.attachmentInput} />
                            </IconButton>
                            <AfitdTypography {...defns.attachmentText}>Attachment</AfitdTypography>
                        </div>

                        <div>
                            <AfitdButton {...defns.cancelButton} />
                            <AfitdButton {...defns.sendButton} />
                        </div>
                    </div>
                </DialogActions>
            </Dialog>
            <AfitdConfirmDialog {...defns.limitDialog} />
            <AfitdConfirmDialog {...defns.confirmCancelDialog} />
            <AfitdMessageDialog {...defns.pageErrorDialog} />
        </>
    );
};

export default SendMessageDialog;
